import React from 'react';
import { Checkbox as AntCheckbox } from 'antd';
import type { CheckboxProps as AntCheckboxProps, CheckboxSemanticStyles } from 'antd/es/checkbox';

import { useUIColors } from '../../theme';
import { cx, type Styleable } from '../../variants';

export interface CheckboxProps
    extends Omit<AntCheckboxProps, 'className' | 'style' | 'styles'>,
        Styleable {
    /**
     * EAA-1804: a fourth visual state on top of checked/unchecked/indeterminate
     * — "permanently on, not yours to change". Always renders CHECKED; there is
     * no locked-OFF state in the sheet, so this prop does not take a value.
     */
    locked?: boolean;
    /**
     * WHY this is locked, e.g. "Luôn bật, không sửa được" — surfaced as the
     * control's `title` (antd's `Checkbox` types don't expose `aria-label` on
     * the wrapper, so `title` is the least-invasive hook available). Caller
     * -supplied: this package holds no domain copy.
     */
    lockedLabel?: string;
    /**
     * Narrowed to the plain object form — antd v6 also allows a resolver
     * function here, which this component doesn't need and which would make
     * merging the locked-state override below need a runtime type check for
     * no benefit.
     */
    styles?: CheckboxSemanticStyles;
}

/**
 * EAA-1820: themed pass-through of antd's `Checkbox`.
 *
 * Added because the kit had no checkbox at all, so every screen that needed one
 * reached for a raw `<input type="checkbox">` and styled it locally — which is
 * how the accounts facet rail, the account table's row selection and the
 * permission matrix ended up with three different-looking boxes for the same
 * control.
 *
 * A thin wrapper on purpose: antd already gives the indeterminate state, the
 * disabled treatment, the label association and keyboard handling, and the
 * palette arrives through `UIAntdConfigProvider`'s tokens. Re-implementing any
 * of that on a bare input is what the component sheet's "one box size per
 * component" rule exists to prevent.
 */
export function Checkbox({
    className,
    style,
    locked = false,
    lockedLabel,
    styles,
    ...rest
}: CheckboxProps): React.ReactElement {
    const colors = useUIColors();

    if (!locked) {
        return (
            <AntCheckbox
                className={cx('ui-checkbox', className)}
                style={style}
                styles={styles}
                {...rest}
            />
        );
    }

    // EAA-1804: antd's own `disabled` look (flat `colorBgContainerDisabled`
    // grey) reads as "temporarily unavailable", which is wrong for a value
    // that is permanently set. `styles.icon`/`styles.label` land as inline
    // style directly on the box and its text span (see `@rc-component/checkbox`
    // and antd's `Checkbox` — both take `style` straight off these semantic
    // slots), which beats antd's CSS-in-JS disabled rule on specificity alone,
    // so a full wrapper `<span>` isn't needed. `disabled` still blocks the
    // native input; only the paint changes.
    const lockedStyles: CheckboxSemanticStyles = {
        ...styles,
        icon: {
            backgroundColor: colors.textSecondary,
            borderColor: colors.textSecondary,
            ...styles?.icon,
        },
        label: { color: colors.text, ...styles?.label },
    };

    return (
        <AntCheckbox
            {...rest}
            className={cx('ui-checkbox', 'ui-checkbox--locked', className)}
            style={style}
            styles={lockedStyles}
            checked
            indeterminate={false}
            disabled
            title={lockedLabel ?? rest.title}
        />
    );
}

Checkbox.Group = AntCheckbox.Group;

export default Checkbox;
